import * as React from 'react'
import { useState } from 'react'

import { observable } from 'mobx'
import { inject, observer } from 'mobx-react'

import NavBar from '~/client/src/desktop/components/NavBar/NavBar'
import { Resource } from '~/client/src/desktop/components/Resource'
import ViewModeToggleGroup from '~/client/src/desktop/components/ViewModeToggleGroup/ViewModeToggleGroup'
import ViewModes from '~/client/src/desktop/enums/ViewModes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import * as Layout from '~/client/src/shared/components/Layout'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import ProjectSetUp from '../ProjectSetUp/ProjectSetUp'
import StruxTagCreateDialog from './components/StruxTagCreateDialog'
import StruxTagsEditPanel from './StruxTagsEditPanel'
import StruxTagsMapLayout from './StruxTagsMapLayout'
import { StruxTagsProvider } from './StruxTagsProvider'

interface StruxTagsProps {
  state?: DesktopInitialState
}

const STRUXTAGS_VIEW_MODES = [ViewModes.List, ViewModes.Map]

const StruxTags: React.FC<StruxTagsProps> = ({ state }) => {
  const projectId = state?.activeProject?.id
  const userId = state?.user?.id
  const [viewState] = useState(() =>
    observable({ viewMode: ViewModes.List as ViewModes }),
  )
  const [isCreateDialogShown, setIsCreateDialogShown] = useState(false)

  if (!projectId || !userId || !state) {
    return null
  }

  return (
    <Layout.View className="struxtags">
      <Layout.Header className="layout-navigation">
        <NavBar />
      </Layout.Header>
      <Layout.Content className="tw:relative tw:overflow-hidden">
        <div className="tw:flex tw:items-center tw:h-full">
          <ProjectSetUp
            className="tw:flex tw:flex-col tw:h-full tw:max-w-fit"
            sidebarOnly
          />
          <div className="tw:flex tw:flex-1 tw:flex-col tw:h-full tw:min-w-0">
            <StruxTagsProvider
              projectId={projectId}
              userId={userId}
              state={state}
            >
              <Layout.View>
                <Layout.Header>
                  <Resource.TopBar>
                    <Resource.SavedViews projectId={projectId} />
                  </Resource.TopBar>
                  <Resource.Header>
                    <Resource.Search />
                    <Resource.Separator />
                    <Resource.Filters />
                    <Resource.Actions>
                      <Resource.DisplayOptions />
                      <ViewModeToggleGroup
                        modes={STRUXTAGS_VIEW_MODES}
                        value={viewState.viewMode}
                        onChange={mode => (viewState.viewMode = mode)}
                      />
                    </Resource.Actions>
                  </Resource.Header>
                </Layout.Header>
                <Layout.Content>
                  {viewState.viewMode === ViewModes.Map ? (
                    <Resource.Content>
                      <StruxTagsMapLayout />
                      <div className="tw:absolute tw:top-1.5 tw:right-4 tw:z-10">
                        <Button
                          variant="default"
                          size="lg"
                          onClick={() => setIsCreateDialogShown(true)}
                        >
                          {Localization.translator.registerStruxTag}
                        </Button>
                      </div>
                    </Resource.Content>
                  ) : (
                    <Resource.Content>
                      <Resource.ActionBar>
                        <div className="tw:flex tw:items-center tw:gap-2">
                          <Resource.CollapseToggle />
                          <Resource.RecordCount
                            label={c => Localization.translator.xStruxTags(c)}
                          />
                          <Resource.DeleteButton
                            confirmationLabel={c =>
                              Localization.translator.confirmDeletionOfStruxTags(
                                c,
                              )
                            }
                          />
                          <Resource.BulkEditButton />
                        </div>
                        <div className="tw:grow-0 tw:relative">
                          <Button
                            variant="default"
                            size="lg"
                            onClick={() => setIsCreateDialogShown(true)}
                          >
                            {Localization.translator.registerStruxTag}
                          </Button>
                        </div>
                      </Resource.ActionBar>
                      <Resource.Table />
                    </Resource.Content>
                  )}
                </Layout.Content>
              </Layout.View>
              <StruxTagsEditPanel />
              <Resource.BulkEditPanel />
              <StruxTagCreateDialog
                isShown={isCreateDialogShown}
                projectId={projectId}
                onClose={() => setIsCreateDialogShown(false)}
              />
            </StruxTagsProvider>
          </div>
        </div>
      </Layout.Content>
      <Layout.Footer>
        <div />
      </Layout.Footer>
    </Layout.View>
  )
}

export default inject('state')(observer(StruxTags))
